import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, X, Loader2, Brain, Volume2, VolumeX, ChevronDown, Pause, Play } from 'lucide-react';
import { toast } from 'sonner';
import axios, { API } from '@/utils/api';

// Voice options from OpenAI TTS
const VOICES = [
  { id: "nova", name: "Nova", description: "Energetic, upbeat" },
  { id: "alloy", name: "Alloy", description: "Neutral, balanced" },
  { id: "coral", name: "Coral", description: "Warm, friendly" },
  { id: "echo", name: "Echo", description: "Smooth, calm" },
  { id: "fable", name: "Fable", description: "Expressive, storytelling" },
  { id: "onyx", name: "Onyx", description: "Deep, authoritative" },
  { id: "sage", name: "Sage", description: "Wise, measured" },
  { id: "shimmer", name: "Shimmer", description: "Bright, cheerful" },
];

// Get Reflection button and modal
export const ReflectionButton = ({ entryId, mood, onReflectionSaved }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reflection, setReflection] = useState(null);
  
  // TTS state
  const [selectedVoice, setSelectedVoice] = useState("nova");
  const [isVoiceDropdownOpen, setIsVoiceDropdownOpen] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const fetchReflection = async () => {
    setLoading(true);
    setIsOpen(true);
    setAudioUrl(null);
    setIsPlaying(false);
    
    try {
      // Use axios instead of fetch to include CSRF token automatically
      const response = await axios.post(`${API}/entries/${entryId}/reflection`, {}, {
        withCredentials: true,
      });
      
      setReflection(response.data.reflection);
      // Notify parent that reflection was saved
      if (onReflectionSaved) {
        onReflectionSaved(response.data.reflection);
      }
      toast.success('Reflection saved to your entry!');
    } catch (error) {
      console.error('Reflection error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to generate reflection';
      toast.error(errorMsg);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const generateSpeech = async () => {
    if (!reflection) return;
    
    setIsGeneratingAudio(true);
    
    try {
      const response = await fetch(`${API}/tts/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: reflection,
          voice: selectedVoice,
          speed: 1.0
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Convert base64 to blob URL
        const byteCharacters = atob(data.audio_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'audio/mp3' });
        const url = URL.createObjectURL(blob);
        
        // Clean up previous audio URL
        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
        }
        
        setAudioUrl(url);
        toast.success(`Playing with ${VOICES.find(v => v.id === selectedVoice)?.name} voice`);
        
        // Auto-play
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.play();
            setIsPlaying(true);
          }
        }, 100);
        
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate audio');
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error('Failed to generate audio');
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  const togglePlayback = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setReflection(null);
    setAudioUrl(null);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  const currentVoice = VOICES.find(v => v.id === selectedVoice);

  return (
    <>
      {/* Button */}
      <button
        onClick={fetchReflection}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-xl hover:opacity-90 transition-all disabled:opacity-50 group"
        style={{ background: 'var(--theme-button-bg)' }}
        data-testid="get-reflection-btn"
      >
        <Brain className="w-4 h-4 group-hover:animate-pulse" />
        <span>Get Reflection</span>
        <Sparkles className="w-3 h-3 opacity-70" />
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div 
            className="rounded-3xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
            style={{
              backgroundColor: 'var(--theme-background)',
              border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)'
            }}
          >
            {/* Header */}
            <div 
              className="flex items-center justify-between p-6"
              style={{ borderBottom: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)' }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="p-2 rounded-xl"
                  style={{ background: 'var(--theme-button-bg)' }}
                >
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ color: 'var(--theme-text)' }}>AI Journal Coach</h2>
                  <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>Your personal reflection</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-[var(--theme-background-secondary)] transition-colors"
              >
                <X className="w-5 h-5" style={{ color: 'var(--theme-text-secondary)' }} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative">
                    <div 
                      className="w-16 h-16 border-4 rounded-full"
                      style={{ borderColor: 'rgba(var(--theme-primary-rgb), 0.2)' }}
                    />
                    <div 
                      className="absolute inset-0 w-16 h-16 border-4 border-t-transparent rounded-full animate-spin"
                      style={{ borderColor: 'var(--theme-primary)', borderTopColor: 'transparent' }}
                    />
                    <Brain className="absolute inset-0 m-auto w-6 h-6 text-[var(--theme-primary)]" />
                  </div>
                  <p className="mt-4" style={{ color: 'var(--theme-text-secondary)' }}>Reflecting on your entry...</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--theme-text-secondary)', opacity: 0.6 }}>This may take a moment</p>
                </div>
              ) : reflection ? (
                <div className="space-y-4">
                  {/* Mood indicator */}
                  {mood && (
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>Reflecting on your</span>
                      <span 
                        className="px-3 py-1 rounded-full text-sm font-medium capitalize"
                        style={{ backgroundColor: 'var(--theme-background-secondary)', color: 'var(--theme-text)' }}
                      >
                        {mood} mood
                      </span>
                    </div>
                  )}
                  
                  {/* Reflection text */}
                  <div 
                    className="rounded-2xl p-6"
                    style={{
                      backgroundColor: 'var(--theme-background-secondary)',
                      border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.1)'
                    }}
                  >
                    <div className="prose prose-invert prose-pink max-w-none">
                      {reflection.split('\n\n').map((paragraph, i) => (
                        <p key={`para-${i}-${paragraph.slice(0, 20)}`} className="leading-relaxed mb-4 last:mb-0" style={{ color: 'var(--theme-text)', opacity: 0.9 }}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>

                  {/* Decorative */}
                  <div className="flex items-center justify-center gap-2 pt-4" style={{ color: 'var(--theme-text-secondary)', opacity: 0.5 }}>
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm">Powered by AI</span>
                    <Sparkles className="w-4 h-4" />
                  </div>
                </div>
              ) : null}
            </div>

            {/* Footer with Voice Controls */}
            {!loading && reflection && (
              <div 
                className="p-4"
                style={{
                  borderTop: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)',
                  backgroundColor: 'rgba(var(--theme-background-rgb), 0.8)'
                }}
              >
                {/* Voice Playback Controls */}
                <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
                  {/* Voice Selector */}
                  <div className="relative">
                    <button
                      onClick={() => setIsVoiceDropdownOpen(!isVoiceDropdownOpen)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl transition-colors min-w-[160px]"
                      style={{
                        backgroundColor: 'var(--theme-background-secondary)',
                        border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)'
                      }}
                      data-testid="voice-selector"
                    >
                      <Volume2 className="w-4 h-4 text-[var(--theme-primary)]" />
                      <span className="text-sm flex-1 text-left" style={{ color: 'var(--theme-text)' }}>{currentVoice?.name}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isVoiceDropdownOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--theme-text-secondary)' }} />
                    </button>
                    
                    {/* Dropdown */}
                    {isVoiceDropdownOpen && (
                      <div 
                        className="absolute bottom-full left-0 mb-2 w-64 rounded-xl shadow-xl overflow-hidden z-10"
                        style={{
                          backgroundColor: 'var(--theme-background)',
                          border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)'
                        }}
                      >
                        <div 
                          className="p-2"
                          style={{ borderBottom: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)' }}
                        >
                          <p className="text-xs px-2" style={{ color: 'var(--theme-text-secondary)', opacity: 0.6 }}>Choose a voice</p>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {VOICES.map((voice) => (
                            <button
                              key={voice.id}
                              onClick={() => {
                                setSelectedVoice(voice.id);
                                setIsVoiceDropdownOpen(false);
                                if (audioUrl) {
                                  URL.revokeObjectURL(audioUrl);
                                  setAudioUrl(null);
                                  setIsPlaying(false);
                                }
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-[var(--theme-background-secondary)] transition-colors flex items-center justify-between"
                              style={{
                                backgroundColor: selectedVoice === voice.id ? 'var(--theme-background-secondary)' : 'transparent'
                              }}
                            >
                              <div>
                                <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{voice.name}</p>
                                <p className="text-xs" style={{ color: 'var(--theme-text-secondary)', opacity: 0.6 }}>{voice.description}</p>
                              </div>
                              {selectedVoice === voice.id && (
                                <div className="w-2 h-2 rounded-full bg-[var(--theme-primary)]" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Play Button */}
                  <div className="flex items-center gap-2">
                    {audioUrl ? (
                      <button
                        onClick={togglePlayback}
                        className="flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-xl hover:opacity-90 transition-opacity"
                        style={{ background: 'var(--theme-button-bg)' }}
                        data-testid="play-pause-btn"
                      >
                        {isPlaying ? (
                          <>
                            <Pause className="w-4 h-4" />
                            <span>Pause</span>
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4" />
                            <span>Resume</span>
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={generateSpeech}
                        disabled={isGeneratingAudio}
                        className="flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
                        style={{ background: 'var(--theme-button-bg)' }}
                        data-testid="read-aloud-btn"
                      >
                        {isGeneratingAudio ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Generating...</span>
                          </>
                        ) : (
                          <>
                            <Volume2 className="w-4 h-4" />
                            <span>Read Aloud</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Audio element (hidden) */}
                {audioUrl && (
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    onEnded={handleAudioEnded}
                    className="hidden"
                  />
                )}
                
                {/* Close Button */}
                <button
                  onClick={handleClose}
                  className="w-full py-3 font-semibold rounded-xl hover:opacity-80 transition-colors"
                  style={{ backgroundColor: 'var(--theme-background-secondary)', color: 'var(--theme-text)' }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReflectionButton;
